import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input, OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ReplaySubject, Subject, Subscription } from 'rxjs';

import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FontsApiService } from '../../services';
import { FontModel } from '../../models';


@Component({
  selector: 'rnpl-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextEditorComponent implements OnDestroy, OnChanges {

  public controlChangesSubscription: Subscription;
  private savedRange: Range | null = null;
  public fontsList: FontModel[] = [];
  public sFontsList = [
    { value: 1, label: '12px' },
    { value: 2, label: '14px' },
    { value: 3, label: '16px' },
    { value: 4, label: '18px' },
    { value: 5, label: '24px' },
    { value: 6, label: '32px' },
    { value: 7, label: '48px' },
  ];
  public presetColors: string[] = [
    '#000000',
    '#3E465F',
    '#F72500',
    '#FF5500',
    '#FFA100',
    '#73B600',
    '#2E4DE3',
    '#7C00E9',
  ];
  public colorPickerOpened: boolean = false;
  public defaultFont: string = 'INTER'; // default font
  public defaultSize: number = 2; // default size
  public defaultColor: string = '#202431'; // default color
  public color: string = this.defaultColor; // default color
  public font: string = this.defaultFont;
  public size: number = this.defaultSize;
  public boldActive = false;
  public italicActive = false;
  public underlineActive = false;

  @Input() public minHeight: number = 280;
  @Input() public editorFontSize: number = 14;
  @Input() public fontsEnabled: boolean = true;
  @Input() public fontsSizeEnabled: boolean = true;
  @Input() public readonly: boolean = false;
  @Input() public isOptional: boolean;
  @Input() public control: FormControl;
  @Input() public textContent: string;
  @Input() public textContentDynamic: string;
  @Input() public title: string;
  @Input() public placeholder: string = 'ECOMMERCE.ENTER_TEXT';

  @Output() public inputEventEmit: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('editor', {static: true}) public editorEl;

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    // This is a Ctrl+A or Cmd+A event
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      // create and add range that contains all container content
      const range = this.document.createRange();
      range.selectNodeContents(this.editorEl.nativeElement);
      this.document.getSelection().removeAllRanges();
      this.document.getSelection().addRange(range);
      this.saveRangeSelection();
    }

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
        setTimeout(() => {
          this.editorEl.nativeElement.focus({ preventScroll: true });
          this.setSelectedAttrs();
        }, 0);
        break;
    }
  }

  @HostListener('paste', ['$event'])
  public onPaste(event): void {
    // paste plain text and skip any html or css content
    event.preventDefault();
    let text = '';
    if (event.clipboardData || event.originalEvent.clipboardData) {
      text = (event.originalEvent || event).clipboardData.getData('text/plain');

      if (this.document.queryCommandSupported('insertText')) {
        this.document.execCommand('insertText', false, text);
      } else {
        this.document.execCommand('paste', false, text);
      }
    }
  }

  @HostListener('drop', ['$event'])
  public onDrop(event: DragEvent): void {
    // skip any dropped content
    event.preventDefault();
  }

  private colorsChangeBuffer$: Subject<string> = new Subject<string>();
  private valueChangeBuffer$: Subject<string> = new Subject<string>();
  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public fontsApiService: FontsApiService,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) public document: Document,
  ) {
    this.colorsChangeBuffer$
      .pipe(
        distinctUntilChanged(),
        debounceTime(250),
        takeUntil(this.destroy$)
      )
      .subscribe((color: string) => {
        this.color = color;
        this.applyCommand('foreColor', color);
      });

    this.valueChangeBuffer$
      .pipe(debounceTime(500))
      .subscribe((value) => this.inputEventEmit.emit(value));

    this.loadFonts();
  }

  public loadFonts(force = false): void {
    this.fontsApiService.getFontsList(force)
      .pipe(takeUntil(this.destroy$))
      .subscribe((fontsList: FontModel[]) => {
        this.fontsList = fontsList
          .filter(f => f.aggregatedSystemFont || f.custom)
          .map(f => ({
            ...f,
            label: this.fontsApiService.generalFontsNamesList[f.name] || f.name
          }));
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('control') && this.control) {
      this.controlSubscription();
    }
    if (changes.hasOwnProperty('textContentDynamic') && this.textContentDynamic) {
      if (!this.textContent && !this.editorValue()) {
        this.textContent = this.textContentDynamic;
        this.addPresetColors();
      }
    }
    if (changes.hasOwnProperty('editorFontSize') && this.editorFontSize) {
      const defaultSize = this.sFontsList.find((i: { value: number; label: string }) => {
        return +i.label.replace('px', '') === this.editorFontSize;
      })
      if (defaultSize && defaultSize.value) {
        this.defaultSize = defaultSize.value;
        this.size = defaultSize.value;
      }
    }
  }

  public controlSubscription(): void {
    if (this.controlChangesSubscription) {
      this.controlChangesSubscription.unsubscribe();
    }
    this.textContent = this.control.value;
    this.addPresetColors();
    this.controlChangesSubscription = this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!this.textContent && !this.editorValue() && value) {
          this.resetTextContent(value);
        }
      });
  }

  public saveRangeSelection(): void {
    const selection = this.document.getSelection();
    if (selection && selection.rangeCount > 0) {
      this.savedRange = selection.getRangeAt(0).cloneRange();
    }
  }

  public editorClicked(): void {
    if (!this.editorValue()) {
      this.applyAllCommands();
    }
  }

  public setSelectedAttrs(): void {
    const selection = this.document.getSelection();

    if (selection && selection.anchorNode && this.editorValue()) {
      if (this.fontsEnabled) {
        this.font = this.findAttr(selection.anchorNode, 'face') || this.defaultFont;
      }
      if (this.fontsSizeEnabled) {
        const size = this.findAttr(selection.anchorNode, 'size');
        this.size = size ? +size : this.defaultSize;
      }
      this.color = this.findAttr(selection.anchorNode, 'color') || this.defaultColor;

      this.boldActive = this.findTag(selection.anchorNode, 'b')
        || this.findStyle(selection.anchorNode, 'fontWeight', 'bold');
      this.italicActive = this.findTag(selection.anchorNode, 'i')
        || this.findStyle(selection.anchorNode, 'fontStyle', 'italic');
      this.underlineActive = this.findTag(selection.anchorNode, 'u')
        || this.findStyle(selection.anchorNode, 'textDecorationLine', 'underline');
    }
  }

  // check elements tree in search of attribute
  public findAttr(startNode: Node, attrName: string): string {
    let parentElement = startNode.parentElement;
    while (parentElement) {
      if (parentElement.className.includes('editor-body')) {
        return null; // stop when reach editor-body
      }
      if (parentElement.attributes.getNamedItem(attrName)) {
        return parentElement.attributes.getNamedItem(attrName).value;
      }
      parentElement = parentElement.parentElement;
    }
    return null;
  }

  // check elements tree in search of tag
  public findTag(startNode: Node, tagName: string): boolean {
    let parentElement = startNode.parentElement;
    while (parentElement) {
      if (parentElement.className.includes('editor-body')) {
        return false; // stop when reach editor-body
      }
      if (parentElement.tagName.toLowerCase() === tagName) {
        return true;
      }
      parentElement = parentElement.parentElement;
    }
    return false;
  }

  public findStyle(startNode: Node, styleAttr: string, styleValue: string): boolean {
    let parentElement = startNode.parentElement;
    while (parentElement) {
      if (parentElement.className.includes('editor-body')) {
        return false; // stop when reach editor-body
      }
      if (parentElement.style[styleAttr] === styleValue) {
        return true;
      }
      parentElement = parentElement.parentElement;
    }
    return false;
  }

  public applyCommand(command: string, option?: string): void {
    this.editorEl.nativeElement.focus({ preventScroll: true });

    if (this.savedRange) {
      const selection = this.document.getSelection();
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    }

    // if value is empty user can select bunch of options before start typing,
    // so we need to apply them all
    if (!this.editorValue()) {
      this.applyAllCommands(command);
      return;
    }

    this.document.execCommand(command, false, option);
    this.saveRangeSelection();
    this.updatedEvent();

    setTimeout(() => {
      this.editorEl.nativeElement.focus({ preventScroll: true });
    }, 0);
  }

  public applyAllCommands(currentCommand?: string): void {
    this.saveRangeSelection();

    this.document.execCommand("delete", false, null);
    if (this.font && this.fontsEnabled) {
      this.document.execCommand('fontName', false, this.font);
    }
    this.document.execCommand('foreColor', false, this.color);
    if (this.size && this.fontsSizeEnabled) {
      this.document.execCommand('fontSize', false, this.size.toString());
    }
    if (this.boldActive || currentCommand === 'bold') {
      this.document.execCommand('bold', false);
    }
    if (this.italicActive || currentCommand === 'italic') {
      this.document.execCommand('italic', false);
    }
    if (this.underlineActive || currentCommand === 'underline') {
      this.document.execCommand('underline', false);
    }

    setTimeout(() => {
      this.editorEl.nativeElement.focus({ preventScroll: true });
    }, 200);
  }

  public setFocusToEditor(): void {
    setTimeout(() => {
      this.editorEl.nativeElement.focus({ preventScroll: true });
      if (this.savedRange) {
        const selection = this.document.getSelection();
        selection.removeAllRanges();
        selection.addRange(this.savedRange);
      }
    }, 0);
  }

  public toggleColorPicker(): void {
    this.colorPickerOpened = !this.colorPickerOpened;
  }

  public onColorPickerPreviewMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  public onColorPickerPreviewClick(event: MouseEvent): void {
    event.preventDefault();
    this.toggleColorPicker();
  }

  public colorChanged(color: string): void {
    if (color) {
      this.colorsChangeBuffer$.next(color);
    }
  }

  public colorPickerClosed(color: string): void {
    if (!this.presetColors.includes(color)) {
      this.presetColors.push(color);
    }
  }

  public addPresetColors(): void {
    const hexColorRegex = /#([0-9A-Fa-f]{3}){1,2}/g;
    if (!this.editorValue() || !this.textContent) { return; }
    const matches = this.textContent.match(hexColorRegex);

    if (matches && matches.length) {
      this.presetColors = [...new Set([...this.presetColors, ...matches])];
    }
  }

  public editorValue(): string {
    return this.editorEl.nativeElement.innerHTML;
  }

  public resetTextContent(text: string): void {
    this.textContent = text;
    this.addPresetColors();
    this.cdr.detectChanges();
  }

  public updatedEvent(): void {
    this.valueChangeBuffer$.next(this.editorValue());
    if (this.control) {
      this.control.patchValue(this.editorValue())
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
